import type { RestaurantEvent } from '@shared/types/restaurants';
import { buildEventTicketPurchasePath } from '@shared/utils/eventUrls';
import { NEW_GUEST_LANDING_RESERVE_PATH } from 'webReservations/paths';
import { Restaurant } from 'webReservations/restaurant/apiHelpers';

interface ModifyReservationParams {
  date: string;
  guestCount: number;
  time: string;
  reservationId: string;
}

type RestaurantSlugs = Pick<Restaurant, 'locationSlug' | 'nameSlug'>;

type GuestLandingPathOptions = {
  useNewGuestLanding: boolean;
};

export const legacyRestaurantBasePath = (
  { locationSlug, nameSlug }: RestaurantSlugs,
  { useNewGuestLanding }: GuestLandingPathOptions,
) =>
  useNewGuestLanding
    ? `/old/${locationSlug}/${nameSlug}`
    : `/${locationSlug}/${nameSlug}`;

export const newGuestLandingBasePath = ({
  locationSlug,
  nameSlug,
}: RestaurantSlugs) => `/${locationSlug}/${nameSlug}`;

/**
 * The redesigned event ticket purchase page's URL identifies the event by a
 * slug stored at creation rather than by id -- e.g.
 * `/palm-springs/normas/events/christmas-party`. See `buildEventTicketPurchasePath`.
 */
export const eventTicketPurchasePath = (
  restaurant: RestaurantSlugs,
  event: Pick<RestaurantEvent, 'name' | 'shift' | 'urlSlug'>,
) => buildEventTicketPurchasePath(restaurant, event);

export const newGuestOfferConfirmationPath = (
  offerId: string,
  restaurant: Pick<Restaurant, 'locationSlug' | 'nameSlug'>,
) => `/${restaurant.locationSlug}/${restaurant.nameSlug}/offers/${offerId}`;

export const guestReservationPath = (
  reservationId: string,
  restaurant: RestaurantSlugs,
) =>
  `/reservations/${restaurant.locationSlug}/${restaurant.nameSlug}/${reservationId}`;

export const guestIncomingOfferPath = (
  reservationId: string,
  offerId: string,
  restaurant: RestaurantSlugs,
) => `${guestReservationPath(reservationId, restaurant)}/offers/${offerId}`;

export const guestOfferSoldPath = (
  reservationId: string,
  offerId: string,
  restaurant: RestaurantSlugs,
) => `${guestIncomingOfferPath(reservationId, offerId, restaurant)}/sold`;

export const newGuestModifyWelcomePath = (
  restaurant: RestaurantSlugs,
  reservationId: string,
  searchParams: URLSearchParams | string | undefined,
) => {
  const search =
    typeof searchParams === 'string'
      ? searchParams
      : (searchParams?.toString() ?? '');
  const query = search ? `?${search}` : '';

  return `${newGuestLandingBasePath(restaurant)}/${reservationId}${query}`;
};

export const newGuestModifyReservePath = (
  restaurant: RestaurantSlugs,
  { date, guestCount, time, reservationId }: ModifyReservationParams,
) => {
  const searchParams = new URLSearchParams({
    date,
    guestCount: String(guestCount),
    time,
  });

  return `${newGuestLandingBasePath(restaurant)}/${reservationId}/${NEW_GUEST_LANDING_RESERVE_PATH}?${searchParams.toString()}`;
};
